<script setup>
/* eslint-disable vue/no-root-v-if */
import { Link, usePage } from '@inertiajs/vue3';
import { PlusCircleIcon } from '@heroicons/vue/24/outline';
import { computed } from 'vue';
import Container from '@/Components/Statamic/Container.vue';
import ListItem from '@/Components/Cards/ListItem.vue';
import Hero from '@/Components/Cards/Hero.vue';
import { useIsPastDateTime } from '@/Composables/localizedDateTime.js';
import { useIsExternalUrl } from '@/Composables/url.js';
import { objectMap, objectSplice } from '@/lib/object.js';

const page = usePage();

/* eslint-disable camelcase */
/* eslint-disable vue/prop-name-casing */
const props = defineProps({
    headline: {
        type: String,
        default: null,
    },
    headline_weight: {
        type: String,
        default: 'h2',
    },
    subheadline: {
        type: String,
        required: false,
        default: null,
    },
    subheadline_weight: {
        type: String,
        required: false,
        default: 'h3',
    },
    extra_link: {
        type: String,
        default: null,
    },
    extra_link_text: {
        type: String,
        default: null,
    },
    extra_link_target: {
        type: String,
        default: null,
    },
    program_packages: {
        type: Object,
        default() {},
    },
});
/* eslint-enable vue/prop-name-casing */

const attendeeCounts = computed(() => page.props.attendeeCounts);

const programPackages = objectMap(props.program_packages, programPackage => {
    programPackage.attendee_count = attendeeCounts.value.find(
        item => item.programPackageSlug === programPackage.slug,
    )?.count || 0;

    return programPackage;
});
/* eslint-enable camelcase */
</script>

<template>
    <div
        v-if="program_packages && Object.keys(program_packages).length > 0"
        class="text-deep-teal-700"
    >
        <Container>
            <component
                :is="headline_weight"
                v-if="headline"
                class="font-semibold text-2xl/tight md:text-4xl"
            >
                {{ headline }}
            </component>

            <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
                <template
                    v-for="(programPackage, index) in objectSplice(programPackages, 0, 2)"
                    :key="index"
                >
                    <Hero
                        class="md:col-span-12 lg:col-span-6"
                        :title="programPackage.title"
                        :teaser="programPackage.teaser"
                        :image="programPackage.glide_card_image_url_avif ? {
                            avif: programPackage.glide_card_image_url_avif,
                            webp: programPackage.glide_card_image_url_webp,
                            jpg: programPackage.glide_card_image_url_jpg,
                            alt: programPackage.teaser_image_alt_with_fallback,
                        } : null"
                        :url="programPackage.external_url || programPackage.url"
                        :type="programPackage.event_format?.event_format_category?.name"
                        :business-areas="programPackage.event_business_areas"
                        :topics="programPackage.event_topics"
                        :attendee-count="programPackage.attendee_count"
                        :start-date="programPackage.start_date"
                        :end-date="programPackage.end_date"
                        :location="programPackage.location?.display_name || (programPackage.event_format?.event_type !== 'external' ? 'Online' : null)"
                        :repetition-dates-count="programPackage.event_container.mode === 'repetition' ? (programPackage.event_container.program_packages_count - 1) : null"
                        :is-inactive="useIsPastDateTime(programPackage.start_date)"
                        :is-external="programPackage.event_format?.event_type === 'external' || programPackage?.event_format.language !== page.props.locale"
                    />
                </template>
            </div>
        </Container>

        <div
            v-if="Object.keys(objectSplice(program_packages, 2)).length > 1"
            class="bg-deep-teal-25 pt-6 pb-16 mt-12"
        >
            <Container>
                <div
                    class="flex flex-wrap justify-between items-center gap-y-2 gap-x-8"
                >
                    <component
                        :is="subheadline_weight"
                        v-if="subheadline"
                        class="font-semibold text-xl/tight md:text-3xl"
                    >
                        {{ subheadline }}
                    </component>

                    <component
                        :is="useIsExternalUrl(extra_link) || extra_link_target === '_blank' ? 'a' : Link"
                        v-if="extra_link && extra_link_text"
                        :href="extra_link"
                        :target="extra_link_target"
                        :rel="extra_link_target === '_blank' ? 'nofollow noopener' : null"
                        class="shrink-0 inline-flex items-center font-semibold text-base gap-x-1.5 hover:underline"
                    >
                        <PlusCircleIcon class="size-5" />

                        {{ extra_link_text }}
                    </component>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
                    <template
                        v-for="(programPackage, index) in objectSplice(programPackages, 2)"
                        :key="index"
                    >
                        <ListItem
                            class="md:col-span-12 lg:col-start-2 lg:col-span-10"
                            :title="programPackage.title"
                            :teaser="programPackage.teaser"
                            :image="programPackage.glide_teaser_image_url_avif ? {
                                avif: programPackage.glide_teaser_image_url_avif,
                                webp: programPackage.glide_teaser_image_url_webp,
                                jpg: programPackage.glide_teaser_image_url_jpg,
                                alt: programPackage.teaser_image_alt_with_fallback,
                            } : null"
                            :url="programPackage.external_url || programPackage.url"
                            :type="programPackage.event_format?.event_format_category?.name"
                            :business-areas="programPackage.event_business_areas"
                            :topics="programPackage.event_topics"
                            :attendee-count="programPackage.attendee_count"
                            :start-date="programPackage.start_date"
                            :end-date="programPackage.end_date"
                            :location="programPackage.location?.display_name || (programPackage.event_format?.event_type !== 'external' ? 'Online' : null)"
                            :repetition-dates-count="programPackage.event_container.mode === 'repetition' ? (programPackage.event_container.program_packages_count - 1) : null"
                            :is-inactive="useIsPastDateTime(programPackage.start_date)"
                            :is-external="programPackage.event_format?.event_type === 'external' || programPackage?.event_format.language !== page.props.locale"
                        />
                    </template>
                </div>
            </Container>
        </div>
    </div>
</template>
